import { useCallback, useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import {
  Slider,
  SliderProps,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import {
  limitPercentageDecimalPlaces,
  resolveValue,
} from './useThresholdInput';

type UseScoreThresholdSliderProps = {
  defaultValue: number;
  thresholdValue: number;
  onChange: (value: number | null) => void;
  sliderProps?: SliderProps;
};

// from what I see defaultValue is redundant here because thresholdValue is set to it in the first place.
// However this componet, and the section that uses it are scheduled to be deleted 🎉 so I'm just gonna go with it for now
export const useScoreThresholdSlider = ({
  defaultValue,
  thresholdValue,
  onChange,
  sliderProps,
}: UseScoreThresholdSliderProps) => {
  const [textFieldValue, setTextFieldValue] = useState<string | number | null>(
    () =>
      R.isNumber(defaultValue)
        ? limitPercentageDecimalPlaces(
            R.clamp(defaultValue, { min: 0, max: 1 }) * 100
          )
        : defaultValue
  );

  const handleInputChange = useCallback(
    (val: number | string | null) => {
      if (val === '' || isFinite(Number(val))) {
        const newValue =
          val === '' ? null : parseFloat((Number(val) / 100).toFixed(3));
        onChange(newValue);
      }
      setTextFieldValue(val);
    },
    [onChange]
  );

  const Component = useMemo(
    () => (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          0
        </Typography>
        <Slider
          value={thresholdValue}
          step={0.01}
          max={1}
          onChange={(_, val) =>
            onChange(Array.isArray(val) ? (val[0] ?? null) : val)
          }
          data-cy="score-threshold-slider"
          {...sliderProps}
        />
        <Typography variant="body2" color="text.secondary">
          100%
        </Typography>
        <TextField
          size="small"
          // Since percentages are limited to one decimal place, user shouldn't be able to enter more than 4 characters
          slotProps={{
            htmlInput: {
              maxLength: 4,
              size: 4,
              'data-cy': 'score-threshold-input',
            },
            input: {
              endAdornment: (
                <Typography variant="body2" color="text.secondary">
                  %
                </Typography>
              ),
            },
          }}
          onBlur={e => {
            const inputValue = e.target.value;

            const numberValue = resolveValue({ inputValue, defaultValue });

            handleInputChange(numberValue);
          }}
          onChange={e => {
            const val = e.target.value;
            if (val === '' || isFinite(Number(val))) {
              const newValue =
                val === '' ? null : parseFloat((Number(val) / 100).toFixed(3));
              onChange(newValue);
            }
            setTextFieldValue(val);
          }}
          value={`${textFieldValue}`}
          sx={{ flexShrink: 0 }}
          disabled={sliderProps?.disabled}
        />
      </Stack>
    ),
    [
      defaultValue,
      handleInputChange,
      onChange,
      textFieldValue,
      thresholdValue,
      sliderProps,
    ]
  );

  useEffect(() => {
    if (thresholdValue) {
      const newInputValue = R.isNumber(thresholdValue)
        ? limitPercentageDecimalPlaces(thresholdValue * 100)
        : thresholdValue;

      if (Number(thresholdValue) !== newInputValue) {
        setTextFieldValue(newInputValue);
      }
    }
  }, [setTextFieldValue, thresholdValue]);

  return { Component };
};
